import { type ButtonHTMLAttributes, type ReactNode } from 'react'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children: ReactNode
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-ember text-ink hover:shadow-button hover:-translate-y-0.5 active:translate-y-0 disabled:bg-haze disabled:text-mute disabled:hover:translate-y-0 disabled:hover:shadow-none',
  secondary:
    'bg-cream text-ink hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 disabled:bg-haze disabled:text-mute disabled:hover:translate-y-0 disabled:hover:shadow-none',
  ghost:
    'bg-transparent text-cream border border-haze hover:border-ember hover:text-ember disabled:opacity-40 disabled:hover:border-haze disabled:hover:text-cream',
  danger:
    'bg-ember/90 text-ink hover:bg-ember hover:shadow-button hover:-translate-y-0.5 active:translate-y-0 disabled:bg-haze disabled:text-mute',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-5 py-2.5 text-sm',
  md: 'px-7 py-3 text-sm',
  lg: 'px-10 py-4 text-base',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  className = '',
  disabled,
  ...props
}: Props) {
  const base =
    'inline-flex items-center justify-center font-display font-medium transition-all duration-300 cursor-pointer'
  const isDisabled = disabled || loading

  return (
    <button
      {...props}
      disabled={isDisabled}
      className={`${base} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {loading && (
        <span className="inline-block w-1.5 h-1.5 bg-current animate-pulse mr-2" />
      )}
      {children}
    </button>
  )
}
